'use client';
import React, { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import useEnergyCalculatorStore, {
  mapStateToEnergyData,
} from '../store/use-energy-calculator-store';
import useInvoiceUploadStore, {
  mapStateToInvoiceData,
} from '@/lib/invoice-upload/store/use-invoice-upload-store';
import { compressData } from '../utils/brotli-integration';
import usePromoCodeStore, {
  validPromoCodeSelector,
} from '@/app/store/use-promo-code-store';
import useEegUserDataStore from '@/app/store/use-eeg-user-data-store';
import useEegDataStore from '@/app/store/use-eeg-data-store';
import { book } from '@/lib/platform/actions/book';
import EegDataSuccess from './eeg-data-success';
import { addToast, Spinner, useDisclosure } from '@heroui/react';

type StartBookingButtonProps = {
  className?: string;
  testId: string;
};

export default function StartBookingButton({
  className,
  testId,
}: StartBookingButtonProps) {
  const t = useTranslations('priceComparison');
  const tEeg = useTranslations('eegData');
  const tEegBooking = useTranslations('eegBooking');
  const customerGroup = useEnergyCalculatorStore((state) => state.customerType);
  const baseBookingUrl = useEnergyCalculatorStore((state) => state.bookingLink);
  const energyCalculatorState = useEnergyCalculatorStore();
  const invoiceUploadState = useInvoiceUploadStore();
  const promoCode = usePromoCodeStore(validPromoCodeSelector);
  const userData = useEegUserDataStore((state) => state.userData);

  const pod = useEegDataStore((state) => state.pod);
  const utility = useEegDataStore((state) => state.utility);
  const location = useEegDataStore((state) => state.location);
  const iban = useEegDataStore((state) => state.iban);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [bookingLink, setBookingLink] = useState('');

  const linkStyles = `btn-padded btn-primary !h-[54px] leading-6.5 ${className == null ? 'min-w-[209px]' : className}`;

  useEffect(() => {
    const generateBookingLink = async () => {
      const energyData = mapStateToEnergyData(energyCalculatorState);
      const invoiceData = mapStateToInvoiceData(invoiceUploadState);
      const compressedData = await compressData({
        customerGroup,
        energyData,
        invoiceData,
        promoCode,
      });
      setBookingLink(
        `${baseBookingUrl}?data=${encodeURIComponent(compressedData)}`
      );
    };
    generateBookingLink();
  }, [
    customerGroup,
    baseBookingUrl,
    energyCalculatorState,
    invoiceUploadState,
    promoCode,
  ]);

  const [isBookingLoading, setIsBookingLoading] = useState(false);

  const handleBooking = async () => {
    if (userData && pod && utility && location) {
      setIsBookingLoading(true);
      try {
        const result = await book({
          userData,
          energyData: energyCalculatorState,
          pod,
          utility,
          location,
          iban,
          promoCode,
        });
        const status = result.status;
        const knownStatus = [400, 403, 409].includes(status) ? status : 500;

        if (status === 200) {
          console.log('Booking successful:', result.data);
          onOpen();
        } else {
          console.error('Booking failed:', result.error);
          addToast({
            title: tEeg(`error.${knownStatus}`),
          });
        }
      } catch (error) {
        console.error('Booking error:', error);
        addToast({
          title: tEeg('manualEntry'),
        });
      } finally {
        setIsBookingLoading(false);
      }
    } else {
      console.warn(
        'Booking aborted: missing data (userData, pod, utility or location)'
      );
    }
  };

  if (userData && pod && utility && location) {
    return (
      <div>
        <button
          data-testid="eegBookingSubmitButton"
          className={`${linkStyles} relative overflow-hidden`}
          onClick={handleBooking}
          disabled={isBookingLoading}
          aria-busy={isBookingLoading}
        >
          {!isBookingLoading && (
            <span data-testid="start-booking-text">
              {tEegBooking('submit')}
            </span>
          )}
          {isBookingLoading && (
            <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Spinner data-testid="start-booking-spinner" />
            </span>
          )}
        </button>
        <EegDataSuccess isOpen={isOpen} onClose={onClose} />
      </div>
    );
  }

  return (
    <a data-testid={testId} className={linkStyles} href={bookingLink}>
      <p data-testid="start-booking-text">{t('book')}</p>
    </a>
  );
}
